import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, SectionHeading } from "../shared/Primitives";
import { RoomGalleryModal, roomImages } from "../RoomGalleryModal";
import { Sprig, Sparkle } from "../shared/Decor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  Disponible: "bg-cocoa text-linen",
  Reservada: "bg-linen text-cocoa border border-cocoa",
  Ocupada: "bg-camel text-linen",
};

export const Rooms = () => {
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const scroller = useRef(null);
  const autoplay = useRef(null);

  useEffect(() => {
    axios.get(`${API}/rooms`).then((r) => setRooms(r.data)).catch(() => setRooms([]));
  }, []);

  const scrollByCards = (dir) => {
    const el = scroller.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8 * dir;
    // loop back to start when reaching the end
    if (dir > 0 && el.scrollLeft + el.clientWidth >= el.scrollWidth - 4) {
      el.scrollTo({ left: 0, behavior: "smooth" });
    } else {
      el.scrollBy({ left: amount, behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (rooms.length === 0) return;
    autoplay.current = setInterval(() => scrollByCards(1), 4500);
    return () => clearInterval(autoplay.current);
  }, [rooms]);

  const pause = () => clearInterval(autoplay.current);
  const resume = () => {
    clearInterval(autoplay.current);
    autoplay.current = setInterval(() => scrollByCards(1), 4500);
  };

  return (
    <section id="habitaciones" data-testid="rooms-section" className="relative overflow-hidden bg-linen py-16 sm:py-20 lg:py-28">
      <Sprig className="absolute top-10 -right-4 w-24 text-camel/25 rotate-12 hidden lg:block" />
      <Sparkle className="absolute bottom-16 left-8 w-7 text-camel/40 hidden sm:block" />
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 relative z-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <SectionHeading
            overline="Tu próximo hogar"
            title="Habitaciones"
            subtitle="Desliza para conocer cada espacio. Todas se entregan amuebladas y con servicios incluidos."
          />
          <div className="flex gap-3 shrink-0">
            <button
              data-testid="rooms-prev"
              onClick={() => scrollByCards(-1)}
              aria-label="Anterior"
              className="w-12 h-12 rounded-full border border-cocoa/40 text-cocoa flex items-center justify-center hover:bg-cocoa hover:text-linen transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              data-testid="rooms-next"
              onClick={() => scrollByCards(1)}
              aria-label="Siguiente"
              className="w-12 h-12 rounded-full border border-cocoa/40 text-cocoa flex items-center justify-center hover:bg-cocoa hover:text-linen transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div
          ref={scroller}
          onMouseEnter={pause}
          onMouseLeave={resume}
          onTouchStart={pause}
          data-testid="rooms-carousel"
          className="mt-14 flex gap-6 overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-smooth pb-4 -mx-2 px-2"
        >
          {rooms.map((room, i) => (
            <div
              key={room.number}
              data-testid={`room-card-${i}`}
              onClick={() => setSelectedRoom(room)}
              className="group snap-start shrink-0 w-[280px] sm:w-[320px] cursor-pointer"
            >
              <div className="relative overflow-hidden rounded-2xl aspect-[4/5]">
                <img
                  src={roomImages(room)[0]}
                  alt={`Habitación ${room.number}`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-espresso/60 via-transparent to-transparent" />
                <span
                  data-testid={`room-status-${i}`}
                  className={`absolute top-4 left-4 inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.15em] uppercase px-3 py-1.5 rounded-full ${STATUS_STYLES[room.status]}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80" />
                  {room.status}
                </span>
                <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between">
                  <div>
                    <span className="text-[11px] font-light text-linen/70 uppercase tracking-wide block">Habitación</span>
                    <span className="font-display text-3xl font-semibold text-linen leading-none">{room.number}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xl font-bold text-linen" data-testid={`room-size-${i}`}>{room.price}</span>
                    <span className="block text-[11px] font-light text-linen/70">{room.size}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <RoomGalleryModal room={selectedRoom} open={!!selectedRoom} onClose={() => setSelectedRoom(null)} />
      </div>
    </section>
  );
};
